import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { api, getPosHeaders } from "./api";
import { usePermissions } from "./PermissionContext";
import { useCompanyBranding } from "./CompanyBrandingContext";

import "./CompanyCategories.css";


function errorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return "No fue posible completar la operación.";
}


function validColor(value, fallback) {
  if (
    typeof value === "string" &&
    typeof CSS !== "undefined" &&
    !/var\(|inherit|currentcolor|unset|initial/i.test(value) &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}


function textColor(background) {
  if (typeof document === "undefined") return "#ffffff";

  const context = document
    .createElement("canvas")
    .getContext("2d");

  if (!context) return "#ffffff";

  context.fillStyle = background;
  context.fillRect(0, 0, 1, 1);

  const [r, g, b] = context.getImageData(0, 0, 1, 1).data;

  const channels = [r, g, b].map((value) => {
    const channel = value / 255;

    return channel <= 0.04045
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });

  const luminance =
    channels[0] * 0.2126 +
    channels[1] * 0.7152 +
    channels[2] * 0.0722;

  return luminance > 0.179 ? "#172033" : "#ffffff";
}


function normalize(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}


function CategoryIcon({ type = "grid" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {type === "grid" && (
        <>
          <rect x="3" y="3" width="7" height="7" rx="2" />
          <rect x="14" y="3" width="7" height="7" rx="2" />
          <rect x="3" y="14" width="7" height="7" rx="2" />
          <rect x="14" y="14" width="7" height="7" rx="2" />
        </>
      )}

      {type === "plus" && <path d="M12 5v14M5 12h14" />}

      {type === "edit" && (
        <>
          <path d="m15 5 4 4M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15l-1 6Z" />
        </>
      )}

      {type === "search" && (
        <>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m16 16 4 4" />
        </>
      )}
    </svg>
  );
}


export default function CompanyCategories() {
  const { company } = useParams();

  const { can, loadingPermissions } = usePermissions();
  const { branding } = useCompanyBranding();

  const canView = can("products.view");
  const canManage = can("products.manage");

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [changingId, setChangingId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const dialogRef = useRef(null);
  const nameRef = useRef(null);
  const busyRef = useRef(false);
  const requestVersion = useRef(0);

  const primary = validColor(
    branding?.primary_color,
    "#475569"
  );

  const secondary = validColor(
    branding?.secondary_color,
    primary
  );

  const theme = {
    "--category-primary": primary,
    "--category-secondary": secondary,
    "--category-on-primary": textColor(primary),
  };

  const loadCategories = useCallback(async () => {
    const version = ++requestVersion.current;

    setLoading(true);
    setLoadError("");

    try {
      const { data } = await api.get(
        "/pos/categories/me",
        {
          headers: getPosHeaders(company),
        }
      );

      if (version !== requestVersion.current) return;

      setCategories(data);
    } catch (error) {
      if (version !== requestVersion.current) return;

      setLoadError(errorMessage(error));
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
      }
    }
  }, [company]);

  useEffect(() => {
    setCategories([]);
    setSearch("");
    setStatusFilter("all");
    setCurrentPage(1);

    if (!loadingPermissions && canView) {
      loadCategories();
    }

    return () => {
      requestVersion.current += 1;
    };
  }, [
    loadingPermissions,
    canView,
    loadCategories,
  ]);

  const summary = useMemo(() => {
    const active = categories.filter(
      (category) => category.is_active
    ).length;

    return {
      total: categories.length,
      active,
      inactive: categories.length - active,
    };
  }, [categories]);

  const filtered = useMemo(() => {
    const term = normalize(search.trim());

    return categories.filter((category) => {
      const matchesSearch =
        !term || normalize(category.name).includes(term);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && category.is_active) ||
        (statusFilter === "inactive" && !category.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [categories, search, statusFilter]);

  const totalRecords = filtered.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalRecords / pageSize)
  );

  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;

  const paginated = filtered.slice(
    startIndex,
    startIndex + pageSize
  );

  const visiblePages = useMemo(() => {
    return [...new Set([
      1,
      totalPages,
      safePage - 1,
      safePage,
      safePage + 1,
    ])]
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((first, second) => first - second);
  }, [safePage, totalPages]);

  function openForm(category = null) {
    if (!canManage || busyRef.current) return;

    setEditingId(category?.id ?? null);
    setName(category?.name || "");
    setFormError("");

    dialogRef.current?.showModal();
    nameRef.current?.focus();
  }

  function closeForm() {
    if (busyRef.current) return;

    dialogRef.current?.close();
    setName("");
    setFormError("");
  }

  async function saveCategory(event) {
    event.preventDefault();

    if (!canManage || busyRef.current) return;

    const cleanName = name.trim();

    if (!cleanName) {
      setFormError("Escribe el nombre de la categoría.");
      return;
    }

    busyRef.current = true;
    setSaving(true);
    setFormError("");

    try {
      const url = editingId === null
        ? "/pos/categories/me"
        : `/pos/categories/me/${editingId}`;

      const config = {
        headers: getPosHeaders(company),
      };

      const { data } = editingId === null
        ? await api.post(url, { name: cleanName }, config)
        : await api.put(url, { name: cleanName }, config);

      setCategories((current) =>
        [
          ...current.filter((item) => item.id !== data.id),
          data,
        ].sort((first, second) =>
          first.name.localeCompare(second.name, "es")
        )
      );

      dialogRef.current?.close();

      toast.success(
        editingId === null
          ? "Categoría creada correctamente."
          : "Categoría actualizada correctamente."
      );
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  }

  async function changeStatus(category) {
    if (!canManage || busyRef.current) return;

    busyRef.current = true;
    setChangingId(category.id);

    try {
      const { data } = await api.patch(
        `/pos/categories/me/${category.id}/status`,
        {
          is_active: !category.is_active,
        },
        {
          headers: getPosHeaders(company),
        }
      );

      setCategories((current) =>
        current.map((item) =>
          item.id === data.id ? data : item
        )
      );

      toast.success(
        data.is_active
          ? "Categoría activada."
          : "Categoría inactivada."
      );
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      busyRef.current = false;
      setChangingId(null);
    }
  }

  if (loadingPermissions) {
    return <p role="status">Validando permisos…</p>;
  }

  if (!canView) {
    return (
      <div className="alert alert-warning">
        No tienes permiso para consultar categorías.
      </div>
    );
  }

  return (
    <section className="pos-categories" style={theme}>
      <header className="pc-heading">
        <div className="pc-heading-content">
          <span className="pc-heading-icon">
            <CategoryIcon />
          </span>

          <div>
            <p className="pc-eyebrow">CATÁLOGO DE PRODUCTOS</p>
            <h1>Categorías</h1>
            <p className="pc-description">
              Organiza y administra las categorías de tu empresa.
            </p>
          </div>
        </div>

        {canManage && (
          <button
            type="button"
            className="pc-button pc-button-primary"
            onClick={() => openForm()}
            disabled={saving || changingId !== null}
          >
            <CategoryIcon type="plus" />
            Nueva categoría
          </button>
        )}
      </header>

      <div className="pc-stats">
        <article className="pc-stat">
          <span>Total de categorías</span>
          <strong>{summary.total}</strong>
          <small>Catálogo de esta empresa</small>
        </article>

        <article className="pc-stat">
          <span>Categorías activas</span>
          <strong>{summary.active}</strong>
          <small>Disponibles para seleccionar</small>
        </article>

        <article className="pc-stat">
          <span>Categorías inactivas</span>
          <strong>{summary.inactive}</strong>
          <small>Conservadas en el catálogo</small>
        </article>
      </div>

      <div className="pc-panel">
        <div className="pc-toolbar">
          <div className="pc-field pc-search">
            <label htmlFor="category-search">
              Buscar categoría
            </label>

            <div className="pc-search-input">
              <CategoryIcon type="search" />

              <input
                id="category-search"
                type="search"
                placeholder="Escribe un nombre…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="pc-field">
            <label htmlFor="category-status">Estado</label>

            <select
              id="category-status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">Todas</option>
              <option value="active">Activas</option>
              <option value="inactive">Inactivas</option>
            </select>
          </div>

          <div className="pc-field">
            <label htmlFor="category-page-size">
              Registros por página
            </label>

            <select
              id="category-page-size"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 registros</option>
              <option value={25}>25 registros</option>
              <option value={50}>50 registros</option>
            </select>
          </div>
        </div>

        {loadError && (
          <div className="pc-error" role="alert">
            <span>{loadError}</span>

            <button
              type="button"
              className="pc-button pc-button-secondary"
              onClick={loadCategories}
              disabled={loading}
            >
              Reintentar
            </button>
          </div>
        )}

        <div className="pc-table-wrapper" aria-busy={loading}>
          <table className="pc-table">
            <caption className="pc-sr-only">
              Categorías de productos de la empresa
            </caption>

            <thead>
              <tr>
                <th scope="col">Categoría</th>
                <th scope="col">Estado</th>
                {canManage && (
                  <th scope="col" className="pc-actions-heading">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={canManage ? 3 : 2}
                    className="pc-empty"
                  >
                    Cargando categorías…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManage ? 3 : 2}
                    className="pc-empty"
                  >
                    <strong>No se encontraron categorías</strong>
                    <span>
                      Cambia los filtros o crea una nueva categoría.
                    </span>
                  </td>
                </tr>
              ) : paginated.map((category) => (
                <tr key={category.id}>
                  <td>
                    <div className="pc-category-name">
                      <span className="pc-category-icon">
                        <CategoryIcon />
                      </span>

                      <strong>{category.name}</strong>
                    </div>
                  </td>

                  <td>
                    <span
                      className={`pc-status ${
                        category.is_active
                          ? "pc-status-active"
                          : "pc-status-inactive"
                      }`}
                    >
                      <span aria-hidden="true" />
                      {category.is_active ? "Activa" : "Inactiva"}
                    </span>
                  </td>

                  {canManage && (
                    <td>
                      <div className="pc-row-actions">
                        <button
                          type="button"
                          className="pc-button pc-button-edit"
                          onClick={() => openForm(category)}
                          disabled={saving || changingId !== null}
                          aria-label={`Editar ${category.name}`}
                        >
                          <CategoryIcon type="edit" />
                          Editar
                        </button>

                        <button
                          type="button"
                          className="pc-button pc-button-secondary"
                          onClick={() => changeStatus(category)}
                          disabled={saving || changingId !== null}
                          aria-label={`${
                            category.is_active
                              ? "Inactivar"
                              : "Activar"
                          } ${category.name}`}
                        >
                          {changingId === category.id
                            ? "Guardando…"
                            : category.is_active
                              ? "Inactivar"
                              : "Activar"}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="pc-pagination">
          <span role="status" aria-live="polite">
            Mostrando{" "}
            <strong>{totalRecords ? startIndex + 1 : 0}</strong>
            {" "}a{" "}
            <strong>
              {Math.min(startIndex + pageSize, totalRecords)}
            </strong>
            {" "}de <strong>{totalRecords}</strong> categorías
          </span>

          <nav aria-label="Paginación de categorías">
            <button
              type="button"
              disabled={safePage === 1 || loading}
              onClick={() => setCurrentPage(safePage - 1)}
              aria-label="Página anterior"
            >
              ‹
            </button>

            {visiblePages.map((page, index) => (
              <span className="pc-page-group" key={page}>
                {index > 0 &&
                  page - visiblePages[index - 1] > 1 && (
                    <span className="pc-page-gap">…</span>
                  )}

                <button
                  type="button"
                  className={page === safePage ? "is-current" : ""}
                  aria-current={
                    page === safePage ? "page" : undefined
                  }
                  aria-label={`Página ${page}`}
                  onClick={() => setCurrentPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              </span>
            ))}

            <button
              type="button"
              disabled={safePage === totalPages || loading}
              onClick={() => setCurrentPage(safePage + 1)}
              aria-label="Página siguiente"
            >
              ›
            </button>
          </nav>
        </footer>
      </div>

      <dialog
        ref={dialogRef}
        className="pc-dialog"
        aria-labelledby="category-dialog-title"
        aria-describedby="category-dialog-description"
        onCancel={(event) => {
          if (busyRef.current) event.preventDefault();
        }}
      >
        <form onSubmit={saveCategory} aria-busy={saving}>
          <header className="pc-dialog-header">
            <span className="pc-dialog-icon">
              <CategoryIcon
                type={editingId === null ? "plus" : "edit"}
              />
            </span>

            <button
              type="button"
              className="pc-dialog-close"
              onClick={closeForm}
              disabled={saving}
              aria-label="Cerrar formulario"
            >
              ×
            </button>

            <p className="pc-eyebrow">CATÁLOGO DE PRODUCTOS</p>

            <h2 id="category-dialog-title">
              {editingId === null
                ? "Nueva categoría"
                : "Editar categoría"}
            </h2>

            <p id="category-dialog-description">
              {editingId === null
                ? "Crea una categoría para organizar tus productos."
                : "Actualiza el nombre de esta categoría."}
            </p>
          </header>

          <div className="pc-dialog-body">
            <div className="pc-field">
              <label htmlFor="category-name">
                Nombre de la categoría
                <span aria-hidden="true"> *</span>
              </label>

              <input
                ref={nameRef}
                id="category-name"
                placeholder="Ejemplo: Helados artesanales"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={100}
                required
                disabled={saving}
                aria-invalid={Boolean(formError)}
                aria-describedby={
                  formError
                    ? "category-form-error"
                    : "category-name-help"
                }
              />

              <small id="category-name-help" className="pc-help">
                Usa un nombre breve y fácil de identificar.
              </small>
            </div>

            <div className="pc-dialog-note">
              {editingId === null
                ? "Esta categoría estará disponible únicamente en tu empresa."
                : "El nuevo nombre se actualizará también en los productos asociados de tu empresa."}
            </div>

            {formError && (
              <div
                id="category-form-error"
                className="pc-error"
                role="alert"
              >
                {formError}
              </div>
            )}
          </div>

          <footer className="pc-dialog-footer">
            <button
              type="button"
              className="pc-button pc-button-secondary"
              onClick={closeForm}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="pc-button pc-button-primary"
              disabled={saving}
            >
              {saving
                ? "Guardando…"
                : editingId === null
                  ? "Crear categoría"
                  : "Guardar cambios"}
            </button>
          </footer>
        </form>
      </dialog>
    </section>
  );
}